import React from 'react';
import { Globe, MousePointer2, Users, Download } from 'lucide-react';

const FIELDS = [
  {
    key: 'is_public',
    label: 'Public Template',
    desc: 'Anyone with a share link can preview this template.',
    Icon: Globe,
  },
  {
    key: 'allow_use_template',
    label: 'Allow Use Template',
    desc: 'Users can click Use Template and enter the editor.',
    Icon: MousePointer2,
  },
  {
    key: 'allow_shared_sessions',
    label: 'Allow Shared Sessions',
    desc: 'Free users can edit in a shared session. Disable to make this Pro-only.',
    Icon: Users,
  },
  {
    key: 'allow_downloads',
    label: 'Allow Downloads',
    desc: 'Users in a shared session can download PNG/JPG.',
    Icon: Download,
  },
];

/**
 * SharingPanel — admin/designer toggle UI for template share_settings.
 *
 * Props:
 *   value     — { is_public, allow_use_template, allow_shared_sessions, allow_downloads }
 *   onChange  — called with the updated value object on each toggle
 *   disabled  — optional, disables all checkboxes
 */
export default function SharingPanel({ value = {}, onChange, disabled = false }) {
  const settings = {
    is_public: value.is_public !== false,
    allow_use_template: value.allow_use_template !== false,
    allow_shared_sessions: value.allow_shared_sessions !== false,
    allow_downloads: value.allow_downloads !== false,
  };

  const handleToggle = (key) => {
    if (disabled || !onChange) return;
    onChange({ ...settings, [key]: !settings[key] });
  };

  return (
    <div
      data-testid="sharing-panel"
      className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3"
    >
      <div>
        <h2 className="text-sm font-semibold text-gray-700">Sharing</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          Control how this template can be shared and used.
        </p>
      </div>
      {FIELDS.map(({ key, label, desc, Icon }) => (
        <label
          key={key}
          className={`flex items-start gap-3 cursor-pointer group ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
        >
          <input
            type="checkbox"
            checked={settings[key]}
            onChange={() => handleToggle(key)}
            disabled={disabled}
            className="mt-1 w-4 h-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
            data-testid={`sharing-toggle-${key}`}
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5">
              <Icon className="w-3.5 h-3.5 text-gray-500" />
              <p className="text-sm font-medium text-gray-800">{label}</p>
            </div>
            <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
          </div>
        </label>
      ))}
    </div>
  );
}
